<script setup lang="ts">
import type { ReportFilters } from '~/models/ReportFilters'
import type { ReportContentVisit, ReportResponse } from '~/models/Report'
import type { BaseItem } from '~/models/Content/BaseItem'
import type { SortMode } from '~/components/report/SortModeToggle.vue'
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faRotateRight } from '@fortawesome/pro-solid-svg-icons'
import { useActivitiesContentQuery } from '~/composables/queries/useActivitiesContentQuery'
import SortModeToggle from '~/components/report/SortModeToggle.vue'
import ReportContentListCard from '~/components/report/ReportContentListCard.vue'
import Pagination from '~/components/report/Pagination.vue'

const props = defineProps<{
  filters?: ReportFilters | undefined
  reportData?: ReportResponse
  isLoading?: boolean
  studentCount?: number
}>()

const emit = defineEmits<{
  open: [payload: { content: BaseItem, visit: ReportContentVisit | undefined }]
  pageChange: [page: number, offset: number]
  sortChange: [ascending: boolean]
}>()

const { t } = useI18n()

const page = ref(1)
const reportData = computed((): ReportResponse =>
  props.reportData ?? { contents: [], assignments: null, total: 0 }
)

const deduplicatedReport = computed(() => {
  const contentMap = new Map<number, ReportContentVisit>()

  for (const contentVisit of reportData.value.contents) {
    const contentId = contentVisit.contentId
    const existing = contentMap.get(contentId)

    if (!existing || new Date(contentVisit.summary.lastActivity) > new Date(existing.summary.lastActivity)) {
      contentMap.set(contentId, contentVisit)
    }
  }

  return {
    contents: Array.from(contentMap.values()),
    assignments: reportData.value.assignments
  }
})

const pageSize = computed(() => props.filters?.limit || 16)

const sortMode = computed<SortMode>({
  get: () => props.filters?.ascending === true ? 'timestamp-asc' : 'timestamp-desc',
  set: (value: SortMode) => {
    const ascending = value === 'timestamp-asc'
    emit('sortChange', ascending)
  }
})

const uniqueContentIds = computed<number[]>(() =>
  deduplicatedReport.value.contents
    .map(contentVisit => contentVisit.contentId)
    .filter(id => Number.isFinite(id) && id > 0)
)

const contentData = computed(() => {
  const visitMap: Record<number, ReportContentVisit> = {}
  const timestampMap: Record<number, number> = {}

  for (const contentVisit of deduplicatedReport.value.contents) {
    if (contentVisit.contentId > 0) {
      visitMap[contentVisit.contentId] = contentVisit

      const time = Date.parse(contentVisit.summary.lastActivity)
      if (Number.isFinite(time)) {
        timestampMap[contentVisit.contentId] = time
      }
    }
  }

  return { visitMap, timestampMap }
})

const visitDataByContentId = computed(() => contentData.value.visitMap)
const lastVisitTimestampByContentId = computed(() => contentData.value.timestampMap)

const contentQueryItems = computed(() => uniqueContentIds.value.map(contentId => ({ contentId })))

const { data: allContents, isLoading, error, refetch } = useActivitiesContentQuery(contentQueryItems, {
  pageSize: 1000
})

const totalItems = computed(() => props.reportData?.total !== undefined ? props.reportData.total : allContents?.value?.length || 0)

const sortedContents = computed(() => {
  const available = allContents?.value ?? []

  if (props.reportData?.total !== undefined) {
    return available
  }

  const start = (page.value - 1) * pageSize.value
  const end = start + pageSize.value
  return available.slice(start, end)
})

const isAnyLoading = computed(() => props.isLoading || isLoading.value)
const resultText = computed(() => totalItems.value === 1 ? t('reports.result.single') : t('reports.result.plural'))
const isEmpty = computed(() => (allContents?.value?.length ?? 0) === 0)
const shouldShowPagination = computed(() => totalItems.value > pageSize.value)

watch(page, (val) => {
  if (props.reportData?.total !== undefined) {
    const offset = (val - 1) * pageSize.value
    emit('pageChange', val, offset)
  }
})

function onOpenCard(payload: { item: BaseItem }) {
  const visitData = visitDataByContentId.value[payload.item.contentId]
  emit('open', { content: payload.item, visit: visitData })
}
</script>

<template>
  <section class="min-h-[40vh] rounded-2xl text-base">
    <div class="mb-5 flex flex-wrap items-center justify-between gap-3 sm:gap-4">
      <div class="text-green-40 text-sm">
        <span v-if="!isAnyLoading">{{ totalItems }} {{ resultText }}</span>
      </div>
      <SortModeToggle
        v-model="sortMode"
      />
    </div>

    <div
      v-if="isAnyLoading"
      aria-live="polite"
      aria-busy="true"
    >
      <div class="flex flex-col gap-4">
        <article
          v-for="n in 8"
          :key="n"
          class="border-gray-10/50 relative flex h-40 w-full items-center overflow-hidden rounded-md border bg-white shadow-lg"
        >
          <div class="bg-gray-10/60 relative size-40 shrink-0 animate-pulse rounded-l-md" />
          <div class="min-w-0 flex-1 space-y-2 p-4">
            <div class="bg-gray-10/60 h-6 w-3/4 animate-pulse rounded" />
            <div class="bg-gray-10/50 h-4 w-full animate-pulse rounded" />
            <div class="bg-gray-10/40 h-4 w-2/3 animate-pulse rounded" />
          </div>
          <div class="flex items-center gap-2 p-4">
            <div class="bg-gray-10/50 h-5 w-16 animate-pulse rounded-md" />
            <div class="bg-gray-10/40 h-4 w-12 animate-pulse rounded" />
          </div>
          <span class="sr-only">{{ t('reports.state.loading') }}</span>
        </article>
      </div>
    </div>
    <div v-else-if="error && !props.isLoading">
      <div class="flex min-h-[20vh] items-center justify-center">
        <div class="max-w-lg rounded-2xl bg-white p-6 text-center shadow-md">
          <h3 class="mb-1 text-lg font-semibold text-gray-100">
            {{ t('reports.state.error.load.title') }}
          </h3>
          <p class="text-gray-60 mb-4">
            {{ t('reports.state.error.load.message') }}
          </p>
          <div class="flex items-center justify-center gap-2">
            <button
              type="button"
              class="bg-seagreen-20 text-seagreen-50 hover:bg-seagreen-30 inline-flex items-center gap-2 rounded-md px-4 py-2 transition focus-visible:ring"
              @click="refetch()"
            >
              <FontAwesomeIcon
                :icon="faRotateRight"
                class="h-4 w-4"
                aria-hidden="true"
              />
              <span>{{ t('reports.state.retry') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <div v-if="isEmpty">
        <div class="flex min-h-[20vh] items-center justify-center">
          <div class="max-w-lg rounded-2xl bg-white p-6 text-center shadow-md">
            <h3 class="mb-1 text-lg font-semibold text-gray-100">
              {{ t('reports.state.error.empty.title') }}
            </h3>
            <p class="text-gray-60">
              {{ t('reports.state.error.empty.message') }}
            </p>
          </div>
        </div>
      </div>

      <div v-else>
        <div class="flex flex-col gap-4">
          <ReportContentListCard
            v-for="item in sortedContents"
            :key="item.contentId"
            :item="item"
            :activity-timestamp="lastVisitTimestampByContentId[item.contentId]"
            :amount-visited="visitDataByContentId[item.contentId]?.summary?.totalActivities"
            :amount-in-group="studentCount"
            @open="onOpenCard($event)"
          />
        </div>
        <div
          v-if="shouldShowPagination"
          class="mt-8 flex justify-end"
        >
          <Pagination
            v-model="page"
            :total="totalItems"
            :page-size="pageSize"
          />
        </div>
      </div>
    </div>
  </section>
</template>
